import { IconMessageQuestion, IconWorkflow } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Card, type CardRootProps } from '@chakra-ui/react/card';
import { Text } from '@chakra-ui/react/text';
import { useEffect, useState } from 'react';

import { CIConfigExpertActionTypes } from './CIConfigExpert';

type AIDrawerEmptyStateProps = {
  action?: CIConfigExpertActionTypes;
};

const actionCardProps: CardRootProps = {
  elevation: false,
  interactive: true,
  paddingSize: 'sm',
  width: '100%',
  textAlign: 'left',
};

const toUIAction = (action?: CIConfigExpertActionTypes): 'create' | 'explain' | undefined => {
  if (action?.startsWith('create')) return 'create';
  if (action?.startsWith('explain')) return 'explain';
  return undefined;
};

const AIDrawerEmptyState = ({ action: initialAction }: AIDrawerEmptyStateProps) => {
  const [action, setAction] = useState<'create' | 'explain' | undefined>(toUIAction(initialAction));

  useEffect(() => {
    setAction(toUIAction(initialAction));
  }, [initialAction]);

  let heading: string;
  let subtext: string;
  if (action === 'create') {
    heading = 'What do you want to build?';
    subtext =
      "Describe what your workflow or pipeline should do. I'll generate the configuration using your project's setup and source code.";
  } else if (action === 'explain') {
    heading = 'What can I help with?';
    subtext =
      "Ask about your workflows, pipelines, triggers, or any part of your Bitrise configuration, I'll explain how it works and suggest improvements if you ask.";
  } else {
    heading = 'How can I help you?';
    subtext = 'Ask me anything about your current workflow or start with a quick action below';
  }

  return (
    <>
      <Box textAlign={action ? 'left' : 'center'} paddingX={action ? '8' : '24'} marginY={action ? 'auto' : undefined}>
        <Text as="h3" textStyle="heading/h3" color="status/ai/text" paddingBlockStart="32" paddingBlockEnd="8">
          {heading}
        </Text>
        <Text textStyle="body/md/regular" color="text/secondary">
          {subtext}
        </Text>
      </Box>
      {!action && (
        <Box display="flex" flexDirection="column" gap="12" width="100%" marginTop="auto">
          <Text as="h6" textStyle="heading/h6" color="text/secondary" textTransform="uppercase">
            Suggested Actions
          </Text>
          <Card.Root asChild {...actionCardProps}>
            <button type="button" onClick={() => setAction('create')}>
              <Card.Body>
                <Box display="flex" alignItems="center" gap="12" mb="12">
                  <Box display="inline-flex" padding="4" background="status/ai/bg" borderRadius="4">
                    <IconWorkflow size="16" color="status/ai/icon" />
                  </Box>
                  <Text textStyle="body/md/semibold">Create a new Workflow</Text>
                </Box>
                <Text textStyle="body/sm/regular" color="text/secondary">
                  Generate a Workflow by describing your CI goals
                </Text>
              </Card.Body>
            </button>
          </Card.Root>
          <Card.Root asChild {...actionCardProps}>
            <button type="button" onClick={() => setAction('explain')}>
              <Card.Body>
                <Box display="flex" alignItems="center" gap="12" mb="12">
                  <Box display="inline-flex" padding="4" background="status/ai/bg" borderRadius="4">
                    <IconMessageQuestion size="16" color="status/ai/icon" />
                  </Box>
                  <Text textStyle="body/md/semibold">Explain config</Text>
                </Box>
                <Text textStyle="body/sm/regular" color="text/secondary">
                  Get a concise overview of your config or any part of it
                </Text>
              </Card.Body>
            </button>
          </Card.Root>
        </Box>
      )}
    </>
  );
};

export default AIDrawerEmptyState;
